import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';
import { getSite } from '../config/sites';
import { serviceAPI, nailTechAPI, depositAPI } from '../services/api';
import { formatUSPhoneInput, toTwilioUSPhone } from '../utils/phone';
import './PublicBookingPage.css';

// Quarter-hour booking slots, restricted to the active site's business hours for
// the selected date (per-day open/close from config/sites.js). Sites without
// configured businessHours keep the legacy 09:00–19:45 window.
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DEFAULT_BUSINESS_HOURS = { start: '09:00', end: '19:45' };

const toMinutes = (time) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const toTimeLabel = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

// Local day-of-week (0=Sun..6=Sat) for a 'YYYY-MM-DD' string. Parsed as local
// so timezone offsets (UTC parse of a bare date) can't shift the weekday.
const dayOfWeek = (dateStr) => {
  if (!dateStr) return new Date().getDay();
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).getDay();
};

const getTimeSlots = (dateStr) => {
  const hours = getSite().businessHours;
  const dayHours = (hours && hours[DAY_KEYS[dayOfWeek(dateStr)]]) || DEFAULT_BUSINESS_HOURS;
  const start = toMinutes(dayHours.start);
  const end = toMinutes(dayHours.end);
  const slots = [];
  for (let t = start; t <= end; t += 15) slots.push(toTimeLabel(t));
  return slots;
};

const formatTimeLabel = (t) => {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${String(m).padStart(2, '0')} ${period}`;
};

/**
 * Public, no-login booking page for salon clients. Embedded in the salon
 * website (iframe). Creates the appointment via the public `createWebsiteBooking`
 * callable (anti-spam included) and, when the selected service requires a
 * deposit, collects it online via Stripe Checkout.
 */
export default function PublicBookingPage() {
  const [formData, setFormData] = useState({
    clientName: '',
    clientPhone: '',
    nailTechId: '',
    serviceId: '',
    appointmentDate: '',
    startTime: '',
    notes: '',
    hpVerify: '', // honeypot — bots fill this, real visitors leave it blank
  });
  const [services, setServices] = useState([]);
  const [nailTechs, setNailTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null); // { deposit: bool, amount, url }
  const openTimeRef = useRef(Date.now());
  const dbId = getSite().databaseId;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [servicesRes, techsRes] = await Promise.all([
          serviceAPI.getAll(),
          nailTechAPI.getAll(),
        ]);
        if (!mounted) return;
        setServices(servicesRes.data || []);
        setNailTechs(techsRes.data || []);
      } catch (err) {
        if (mounted) setError('Unable to load booking options. Please try again.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const selectedService = services.find((s) => s._id === formData.serviceId) || null;
  const selectedTech = nailTechs.find((t) => t._id === formData.nailTechId) || null;
  const depositInfo = depositAPI.computeDeposit(selectedService);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'clientPhone' ? formatUSPhoneInput(value) : value,
      // Slots depend on the day of week — clear a now-invalid time on date change.
      ...(name === 'appointmentDate' ? { startTime: '' } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(null);
    try {
      const createBooking = httpsCallable(functions, 'createWebsiteBooking');
      const res = await createBooking({
        databaseId: dbId,
        clientName: formData.clientName.trim(),
        clientPhone: toTwilioUSPhone(formData.clientPhone),
        appointmentDate: formData.appointmentDate,
        startTime: formData.startTime,
        serviceId: formData.serviceId,
        serviceName: selectedService?.name || '',
        nailTechId: formData.nailTechId,
        nailTechName: selectedTech?.name || '',
        notes: formData.notes,
        company: formData.hpVerify,
        formOpenMs: Date.now() - openTimeRef.current,
      });

      const appointmentId = res.data?.appointmentId;
      if (depositInfo && appointmentId) {
        const methods = await depositAPI.getPublicMethods();
        setSuccess({
          deposit: true,
          amount: depositInfo.amount,
          appointmentId,
          methods: methods.data || {},
          confirmed: false,
          method: '',
        });
      } else {
        setSuccess({ deposit: false });
      }
    } catch (err) {
      setError(
        err?.details?.message ||
          err?.details?.error ||
          err?.message ||
          'Booking could not be completed. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDeposit = async (method) => {
    if (!success?.appointmentId) return;
    setError('');
    try {
      await depositAPI.confirmPayment(success.appointmentId, method);
      setSuccess((prev) => ({ ...prev, confirmed: true, method }));
    } catch (err) {
      setError(err?.message || 'Failed to confirm deposit. Please try again.');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="public-booking">
        <div className="public-card">
          <p className="public-muted">Loading booking options…</p>
        </div>
      </div>
    );
  }

  if (success) {
    const methods = success.methods || {};
    const hasZelle = !!(methods.zelleEmail || methods.zellePhone);
    const hasCashApp = !!methods.cashappTag;
    return (
      <div className="public-booking">
        <div className="public-card">
          <div className="public-success">✓</div>
          <h2>{success.confirmed ? 'Deposit Marked as Sent' : 'Appointment Requested!'}</h2>

          {!success.deposit && (
            <p>No deposit is required for this service. We'll contact you shortly to confirm your appointment.</p>
          )}

          {success.deposit && success.confirmed && (
            <p>
              Thank you! We've recorded that you sent your ${success.amount} deposit via{' '}
              {success.method === 'zelle' ? 'Zelle' : 'CashApp'}. We'll confirm your appointment
              once we verify the payment.
            </p>
          )}

          {success.deposit && !success.confirmed && (
            <>
              <p>
                A <strong>${success.amount} deposit</strong> is required to confirm your
                appointment. Send it using one of the options below, then tap{" "}
                <strong>"I've sent the deposit"</strong>. Please include your name so we can match
                the payment to your booking.
              </p>

              {hasZelle && (
                <div className="payment-method">
                  <h4>Zelle</h4>
                  {methods.zelleEmail && <p>Send to: <strong>{methods.zelleEmail}</strong></p>}
                  {methods.zellePhone && <p>Send to: <strong>{methods.zellePhone}</strong></p>}
                  <button type="button" className="public-btn" onClick={() => handleConfirmDeposit('zelle')}>
                    I sent it via Zelle
                  </button>
                </div>
              )}

              {hasCashApp && (
                <div className="payment-method">
                  <h4>CashApp</h4>
                  <p>Send to: <strong>${methods.cashappTag}</strong></p>
                  <button type="button" className="public-btn" onClick={() => handleConfirmDeposit('cashapp')}>
                    I sent it via CashApp
                  </button>
                </div>
              )}

              {!hasZelle && !hasCashApp && (
                <p className="public-muted">We'll contact you shortly to arrange the deposit.</p>
              )}
            </>
          )}

          <button
            type="button"
            className="public-btn secondary"
            onClick={() => {
              setSuccess(null);
              setFormData({
                clientName: '',
                clientPhone: '',
                nailTechId: '',
                serviceId: '',
                appointmentDate: '',
                startTime: '',
                notes: '',
                hpVerify: '',
              });
              openTimeRef.current = Date.now();
            }}
          >
            Book Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-booking">
      <div className="public-card">
        <h2>Book an Appointment</h2>
        <p className="public-sub">
          Choose your service, technician, and time. A deposit may be required for some services.
        </p>

        {error && <div className="public-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Honeypot — hidden from real users, catches bots. Named obscurely
              so browser autofill won't populate it (autofill fills 'company'). */}
          <div className="public-honeypot" aria-hidden="true">
            <label htmlFor="hpVerify">Verification</label>
            <input
              type="text"
              id="hpVerify"
              name="hp_verify"
              value={formData.hpVerify}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pb-name">Your Name *</label>
            <input
              type="text"
              id="pb-name"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              required
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pb-phone">Phone *</label>
            <input
              type="tel"
              id="pb-phone"
              name="clientPhone"
              value={formData.clientPhone}
              onChange={handleChange}
              placeholder="+1-702-555-1234"
              required
              autoComplete="tel"
              maxLength="15"
            />
          </div>

          <div className="form-group">
            <label htmlFor="pb-service">Service *</label>
            <select id="pb-service" name="serviceId" value={formData.serviceId} onChange={handleChange} required>
              <option value="">Select a service</option>
              {services.map((service) => (
                <option key={service._id} value={service._id}>
                  {service.name}
                </option>
              ))}
            </select>
            {depositInfo && (
              <p className="deposit-note">
                💳 Deposit required: ${depositInfo.amount}
                {depositInfo.type === 'percent' ? ` (${depositInfo.value}% of service price)` : ''} —
                payable via Zelle or CashApp.
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="pb-tech">Technician</label>
            <select id="pb-tech" name="nailTechId" value={formData.nailTechId} onChange={handleChange}>
              <option value="">Any available technician</option>
              {nailTechs.map((tech) => (
                <option key={tech._id} value={tech._id}>
                  {tech.name}
                </option>
              ))}
            </select>
          </div>

          <div className="public-row">
            <div className="form-group">
              <label htmlFor="pb-date">Date *</label>
              <input
                type="date"
                id="pb-date"
                name="appointmentDate"
                min={today}
                value={formData.appointmentDate}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="pb-time">Time *</label>
              <select
                id="pb-time"
                name="startTime"
                value={formData.startTime}
                onChange={handleChange}
                required
              >
                <option value="">Pick a time</option>
                {getTimeSlots(formData.appointmentDate).map((t) => (
                  <option key={t} value={t}>
                    {formatTimeLabel(t)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="pb-notes">Notes (optional)</label>
            <textarea id="pb-notes" name="notes" rows="3" value={formData.notes} onChange={handleChange} />
          </div>

          <button type="submit" className="public-btn" disabled={submitting}>
            {submitting ? 'Booking…' : 'Book Appointment'}
          </button>
        </form>
      </div>
    </div>
  );
}
